import { useState } from "react";
import { Link, useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { PageHero } from "@/components/layout/page-hero";
import { SectionHeading } from "@/components/ui/section-heading";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { Star, BadgeCheck, CalendarDays, MapPin } from "lucide-react";
import { wavesVideo, parallaxBg, wavesPoster } from "@/lib/media-assets";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { KUALOA_PROPERTY_SLUG } from "@shared/property-context";
import type { PublicProperty } from "@shared/schema";
import {
  useSEO,
  useSEOSettings,
  generateBreadcrumbSchema,
  generateSpeakableSchema,
  generateFAQSchema,
} from "@/lib/seo-utils";

interface PublicReview {
  id: string;
  guest_name: string;
  location: string | null;
  rating: number;
  review_date: string;
  stay_start_date: string | null;
  stay_end_date: string | null;
  trip_type: string | null;
  review_text: string | null;
  would_recommend: boolean | null;
  is_featured: boolean | null;
  verified_guest: boolean | null;
}

interface ReviewsSummary {
  averageRating: number;
  totalReviews: number;
  reviewCounts: Record<string, number>;
}

/**
 * Trip type drives the section order because it is the decision a reader is
 * actually making - "will this suit our family / our group?" - rather than an
 * arbitrary sort. Anything without a recognised trip type falls into "All stays"
 * so no genuine review is hidden.
 */
const TRIP_SECTIONS: { key: string; title: string; lede: string }[] = [
  {
    key: "Family",
    title: "Families",
    lede: "Stays with children, where beach access from the door and a full kitchen tend to matter most.",
  },
  {
    key: "Group",
    title: "Groups",
    lede: "Larger parties using all three bedrooms, up to the eight-guest maximum.",
  },
  {
    key: "Couple",
    title: "Couples",
    lede: "Two-guest stays, usually booked for the quiet end of the windward coast.",
  },
];

function formatMonth(value?: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-[2px]" role="img" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${n <= rating ? "fill-sun text-sun" : "text-sand-deep"}`}
          aria-hidden="true"
        />
      ))}
    </span>
  );
}

function ReviewCard({ review }: { review: PublicReview }) {
  const stayed = formatMonth(review.stay_start_date) || formatMonth(review.review_date);
  return (
    <article className="h-full bg-sand border border-sand-deep p-7 md:p-8 flex flex-col">
      <div className="flex items-center justify-between gap-4 mb-4">
        <Stars rating={review.rating} />
        {review.verified_guest && (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-sans font-semibold uppercase tracking-[0.16em] text-driftwood">
            <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Verified stay
          </span>
        )}
      </div>

      {review.review_text && (
        <blockquote className="text-[15px] text-ink-soft font-sans leading-relaxed flex-1">
          {review.review_text}
        </blockquote>
      )}

      <footer className="mt-6 pt-5 border-t border-sand-deep/70">
        <p className="font-serif text-ink text-[17px] leading-tight">{review.guest_name}</p>
        <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-driftwood font-sans">
          {review.location && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3 w-3" aria-hidden="true" />
              {review.location}
            </span>
          )}
          {stayed && (
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="h-3 w-3" aria-hidden="true" />
              Stayed {stayed}
            </span>
          )}
        </p>
      </footer>
    </article>
  );
}


/** Six reviews at a time. Forty-eight cards in one column made the phone page
 *  the longest on the site; the rest are one tap away. */
function ReviewGrid({ items, pageSize = 6 }: { items: Array<{ id: string } & Record<string, any>>; pageSize?: number }) {
  const [shown, setShown] = useState(pageSize);
  const visible = items.slice(0, shown);
  const remaining = items.length - visible.length;
  return (
    <>
      <div className="grid md:grid-cols-2 gap-5 md:gap-6">
        {visible.map((review) => (
          <ScrollReveal key={review.id}>
            <ReviewCard review={review as any} />
          </ScrollReveal>
        ))}
      </div>
      {remaining > 0 && (
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => setShown((n) => n + pageSize * 2)}
            className="inline-flex min-h-[44px] items-center justify-center border border-sand-deep bg-shell px-6 font-sans text-[11px] uppercase tracking-[0.24em] text-forest transition-colors hover:bg-sand"
            data-testid="reviews-show-more"
          >
            Show {Math.min(remaining, pageSize * 2)} more of {remaining}
          </button>
        </div>
      )}
    </>
  );
}

export default function ReviewsPage() {
  const { seoSettings } = useSEOSettings();
  const params = useParams<{ slug?: string }>();
  const slug = params.slug;
  const { propertyRoutes } = useFeatureFlags();
  const isKualoaHub = !slug || slug === KUALOA_PROPERTY_SLUG;
  const propertyFilter = slug ? { property: slug } : {};

  const { data: listedProperty } = useQuery<PublicProperty>({
    queryKey: ["/api/properties", slug],
    enabled: Boolean(slug) && !isKualoaHub,
    retry: false,
  });

  const { data: reviews } = useQuery<PublicReview[]>({
    queryKey: ["/api/reviews/public", propertyFilter],
    retry: false,
  });
  const { data: summary } = useQuery<ReviewsSummary>({
    queryKey: ["/api/reviews/summary", propertyFilter],
    retry: false,
  });

  const all = reviews ?? [];
  const total = summary?.totalReviews ?? all.length;
  const average = summary?.averageRating;

  const grouped = TRIP_SECTIONS.map((section) => ({
    ...section,
    items: all.filter((r) => (r.trip_type || "").toLowerCase() === section.key.toLowerCase()),
  })).filter((section) => section.items.length > 0);

  const ungrouped = all.filter(
    (r) => !TRIP_SECTIONS.some((s) => s.key.toLowerCase() === (r.trip_type || "").toLowerCase()),
  );

  // Schema mirrors exactly what is rendered: the same reviews, the same counts.
  // AggregateRating is omitted entirely rather than guessed when the summary has
  // not loaded, because publishing a rating the page does not display is the
  // structured-data problem this site already had once.
  const reviewNodes = all
    .filter((r) => r.review_text)
    .map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.guest_name },
      datePublished: r.review_date,
      reviewBody: r.review_text,
      reviewRating: {
        "@type": "Rating",
        ratingValue: String(r.rating),
        bestRating: "5",
        worstRating: "1",
      },
    }));

  const propertyTitle = isKualoaHub ? "The Kualoa Beach House" : (listedProperty?.title || "This Oahu vacation home");
  const propertyLocation = isKualoaHub
    ? "Ka'a'awa, Oahu"
    : (listedProperty?.general_location || "Oahu");
  const stayHref = isKualoaHub
    ? (propertyRoutes ? `/properties/${KUALOA_PROPERTY_SLUG}` : "/stay")
    : `/properties/${slug}`;
  const bookHref = isKualoaHub
    ? (propertyRoutes ? `/properties/${KUALOA_PROPERTY_SLUG}/book` : "/booking")
    : `/properties/${slug}/book`;
  const reviewsPath = isKualoaHub ? "/reviews" : `/properties/${slug}/reviews`;
  const reviewsCanonical = `https://vacationrentaloahu.co${reviewsPath}`;

  const propertyFaqs = isKualoaHub ? [] : [
    {
      question: `Are these reviews for ${propertyTitle}?`,
      answer: `Yes. Every review on this page was left by a guest who booked and stayed at ${propertyTitle} in ${propertyLocation}. Ratings and wording appear as the guest submitted them.`,
    },
    {
      question: `How do I book ${propertyTitle} after reading reviews?`,
      answer: `Check dates on the ${propertyTitle} booking page and reserve direct through Vacation Rental Oahu. Direct booking is the same stay guests describe in these reviews.`,
    },
  ];

  const breadcrumbItems = isKualoaHub
    ? [
        { name: "Home", url: "/" },
        { name: "Guest Reviews", url: "/reviews" },
      ]
    : [
        { name: "Home", url: "/" },
        { name: "Our Homes", url: "/properties" },
        { name: propertyTitle, url: stayHref },
        { name: "Guest Reviews", url: reviewsPath },
      ];

  const structuredData: Record<string, unknown>[] = [
    generateBreadcrumbSchema(breadcrumbItems),
    generateSpeakableSchema(["h1", "h2"]),
    ...(propertyFaqs.length ? [generateFAQSchema(propertyFaqs)] : []),
  ];

  if (total > 0 && typeof average === "number") {
    structuredData.push({
      "@context": "https://schema.org",
      "@type": "LodgingBusiness",
      "@id": isKualoaHub
        ? "https://vacationrentaloahu.co/#lodging"
        : `https://vacationrentaloahu.co/properties/${slug}#lodging`,
      name: propertyTitle,
      url: isKualoaHub ? "https://vacationrentaloahu.co" : `https://vacationrentaloahu.co${stayHref}`,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: String(average),
        reviewCount: String(total),
        bestRating: "5",
        worstRating: "1",
      },
      ...(reviewNodes.length > 0 && { review: reviewNodes }),
    });
  }

  const seoHead = useSEO({
    pageType: "reviews",
    title: isKualoaHub
      ? "Guest Reviews | The Kualoa Beach House, Oahu"
      : `Guest Reviews | ${propertyTitle}, Oahu`,
    description:
      average && total
        ? `Read all ${total} verified guest reviews of ${propertyTitle}, our vacation rental in ${propertyLocation}. Rated ${average} out of 5 by guests who booked direct.`
        : `Read verified guest reviews of ${propertyTitle}, a vacation rental in ${propertyLocation}.`,
    canonical: reviewsCanonical,
    ogType: "website",
    twitterCard: "summary_large_image",
    structuredData: seoSettings ? structuredData : undefined,
  });

  return (
    <>
      {seoHead}
      <div className="min-h-screen bg-shell pb-24 lg:pb-0 selection:bg-sun selection:text-shell">
        <Header />
        <main id="main-content">
          <PageHero
            eyebrow="Guest Reviews"
            title="Guest reviews"
            subtitle={
              average && total
                ? `${average} out of 5 across ${total} reviews from guests who stayed at ${isKualoaHub ? "our Ka'a'awa beach house" : propertyTitle}.`
                : `Reviews from guests who stayed at ${isKualoaHub ? "our Ka'a'awa beach house" : propertyTitle}.`
            }
            videoSrc={wavesVideo}
            posterSrc={wavesPoster}
            imageAlt={
              isKualoaHub
                ? "Beachfront ocean views at The Kualoa Beach House in Ka'a'awa, Oahu"
                : `${propertyTitle} in ${propertyLocation}`
            }
          />

          <section className="py-16 md:py-24 bg-shell">
            <div className="container mx-auto px-6 max-w-5xl">
              <Breadcrumbs
                items={breadcrumbItems}
                className="mb-14 justify-center"
              />

              {/*
                Ownership disclosure, stated before any review is read. This hub is
                published by the property itself, so presenting it as an
                independent review site would be misleading - and the tactic
                playbook explicitly rules that out.
              */}
              <ScrollReveal>
                <div className="mb-16 border border-sand-deep bg-sand p-7 md:p-9 max-w-3xl mx-auto">
                  <p className="text-[15px] text-ink-soft font-sans leading-relaxed">
                    <strong className="font-normal text-ink">About this page.</strong>{" "}
                    This page is published and operated by {propertyTitle}. It is not
                    an independent review site. Every review below was left by a guest who
                    booked and stayed at the property, is published with their permission, and
                    appears with its original wording and rating unchanged. We do not screen
                    guests before inviting feedback, and we do not remove a review for being
                    critical.
                  </p>
                </div>
              </ScrollReveal>

              {total > 0 && typeof average === "number" && (
                <ScrollReveal>
                  <div className="mb-20 text-center">
                    <p className="font-serif text-forest text-[56px] leading-none">{average}</p>
                    <div className="mt-3 flex justify-center">
                      <Stars rating={Math.round(average)} />
                    </div>
                    <p className="mt-3 text-[13px] font-sans uppercase tracking-[0.2em] text-driftwood">
                      {total} guest reviews
                    </p>
                  </div>
                </ScrollReveal>
              )}

              {grouped.map((section) => (
                <div key={section.key} className="mb-20">
                  <SectionHeading
                    eyebrow={`${section.items.length} ${section.items.length === 1 ? "stay" : "stays"}`}
                    title={section.title}
                    lede={section.lede}
                    align="left"
                    className="mb-10"
                  />
                  <ReviewGrid items={section.items as any} />
                </div>
              ))}

              {ungrouped.length > 0 && (
                <div className="mb-20">
                  <SectionHeading
                    eyebrow={`${ungrouped.length} ${ungrouped.length === 1 ? "stay" : "stays"}`}
                    title="All stays"
                    align="left"
                    className="mb-10"
                  />
                  <ReviewGrid items={ungrouped as any} />
                </div>
              )}

              {propertyFaqs.length > 0 && (
                <div className="mb-20 max-w-3xl">
                  <SectionHeading
                    eyebrow="Common questions"
                    title="About these reviews"
                    align="left"
                    className="mb-10"
                  />
                  <div className="space-y-8">
                    {propertyFaqs.map((faq) => (
                      <section key={faq.question}>
                        <h2 className="font-serif text-[22px] text-ink mb-3">{faq.question}</h2>
                        <p className="font-sans text-[15px] leading-relaxed text-ink-soft">{faq.answer}</p>
                      </section>
                    ))}
                  </div>
                </div>
              )}

              <div className="h-px bg-sand-deep w-full mb-14" />

              <div className="text-center max-w-2xl mx-auto">
                <p className="text-luxury-lg text-ink-soft leading-relaxed mb-6">
                  Every stay above was booked direct. See the house itself on the{" "}
                  <Link href={stayHref} className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300">property page</Link>
                  , read the{" "}
                  <Link href="/policies" className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300">cancellation policy</Link>
                  , or check dates on our{" "}
                  <Link href={isKualoaHub ? "/" : stayHref} className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300">Oahu beachfront vacation rental</Link>.
                </p>
                <Link
                  href={bookHref}
                  data-primary-book-cta
                  className="inline-flex items-center justify-center bg-sun text-shell hover:bg-sun-hover font-sans tracking-wide px-8 py-4 transition-colors duration-300"
                >
                  Check availability
                </Link>
              </div>
            </div>
          </section>
        </main>
        <StickyMobileBookCta />
      <Footer />
      </div>
    </>
  );
}
